import { Check, ChevronDown, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'

export interface FilterPillOption {
  value: string
  label: string
  icon?: string
}

interface FilterPillProps {
  label: string
  options: FilterPillOption[]
  selected: string[]
  onChange: (values: string[]) => void
  /** Optional counts per option value */
  counts?: Record<string, number>
}

export function FilterPill({
  label,
  options,
  selected,
  onChange,
  counts,
}: FilterPillProps) {
  const isAllSelected = selected.length === 0
  const selectedLabels = selected
    .map((v) => options.find((o) => o.value === v)?.label)
    .filter(Boolean)

  const displayText = isAllSelected
    ? 'All'
    : selectedLabels.length <= 2
      ? selectedLabels.join(', ')
      : `${selectedLabels[0]} +${selectedLabels.length - 1}`

  const handleToggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value))
    } else {
      onChange([...selected, value])
    }
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-8 gap-1.5 text-xs font-normal',
            !isAllSelected && 'border-primary/50 bg-primary/5'
          )}
        >
          <span className="text-muted-foreground">{label}:</span>
          <span className="font-medium">{displayText}</span>
          <ChevronDown className="h-3 w-3 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandList>
            <CommandEmpty>No options found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selected.includes(option.value)
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => handleToggle(option.value)}
                  >
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible'
                      )}
                    >
                      <Check className="h-3 w-3" />
                    </div>
                    {option.icon && <span className="mr-1.5">{option.icon}</span>}
                    <span className="flex-1">{option.label}</span>
                    {counts && counts[option.value] !== undefined && (
                      <span className="ml-auto text-xs text-muted-foreground">
                        {counts[option.value]}
                      </span>
                    )}
                  </CommandItem>
                )
              })}
            </CommandGroup>
            {selected.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => onChange([])}
                    className="justify-center text-center text-xs"
                  >
                    <X className="mr-1 h-3 w-3" />
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
